import { useCallback, useContext, useEffect, useState } from 'react';
import { Broadcast, Message, useGetChatMessagesQuery } from '../features/api/apiSlice';
import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { useSocketApi } from './useSocketApi';
import { SendChatMessageAction, SocketApiActionType, VkCaptchaErrorAction } from '../types/socket.api.types';

interface UseChatParams {
  broadcastId: Broadcast['id'];
  chatToken?: string;
  studioBroadcastId?: Broadcast["id"];
}

export const useChat = ({ broadcastId, chatToken, studioBroadcastId }: UseChatParams) => {
  const { connectToChatSocket, chatSocketIdentifier } = useContext(SocketContext);

  const [ captchaError, setCaptchaError ] = useState<VkCaptchaErrorAction["payload"] | null>(null);
  const [ messages, setMessages ] = useState<Message[]>([]);
  const currentBroadcastId = studioBroadcastId ? studioBroadcastId : broadcastId;

  // * Get messages from chat
  const { isSuccess, data: fetchedMessages, refetch: refetchMessages } = useGetChatMessagesQuery({
    id: String(currentBroadcastId),
    token: chatToken,
  });

  useEffect(() => {
    if (isSuccess && fetchedMessages?.response) {
      setMessages([...fetchedMessages.response].reverse());
    }
  }, [isSuccess, fetchedMessages]);

  useEffect(() => {
    connectToChatSocket(Number(currentBroadcastId));
    return () => setCaptchaError(null);
  }, [currentBroadcastId]);

  const { sendMessage } = useSocketApi({
    onNewChatMessage: (payload) => {
      setMessages(prev => [...prev, payload]);
    },
    onVkCaptchaError: setCaptchaError
  });

  const handleSendChatMessage = useCallback((data: SendChatMessageAction["payload"], targetPlatforms?: SendChatMessageAction["target_platform"]) => {
    if (!chatSocketIdentifier) return;

    sendMessage({
      action: SocketApiActionType.SEND_NEW_CHAT_MESSAGE,
      identifier: chatSocketIdentifier,
      payload: {
        ...data,
        broadcast_id: currentBroadcastId,
      },
      target_platform: targetPlatforms,
    });
  }, [chatSocketIdentifier]);

  return { messages, captchaError, handleSendChatMessage, setCaptchaError, refetchMessages };
};